import {
  COMPUTO_SI,
  DISPONIBLE,
  ESTATUS_COTIZACION_COMPUTO,
  ESTATUS_PRODUCTO,
  SELECCION,
} from "../constants/gestionCotizacion.constants";
import type {
  Cotizacion,
  DetalleCotizacionProveedor,
  ProveedorCotizacion,
  Recotizacion,
} from "../types/gestionCotizacion.types";

export const esCotizacionComputo = (c?: Cotizacion | null) => c?.computo === COMPUTO_SI;

export const esCotizacionEnviada = (c?: Cotizacion | null) =>
  Number(c?.estatus) === ESTATUS_COTIZACION_COMPUTO.ENVIADA_AUTORIZAR;

/** id_inventario de los productos ya autorizados (completos) */
export const obtenerInventariosAutorizados = (c?: Cotizacion | null): Set<number> => {
  if (!esCotizacionComputo(c)) return new Set();

  return new Set(
    (c?.productos_requisicion ?? [])
      .filter((p) => p.completo)
      .map((p) => Number(p.id_inventario))
  );
};

/** Proveedores que ya tienen un detalle (respondido o no) por cada producto */
export const obtenerProveedoresConDetallePorProducto = (
  c?: Cotizacion | null
): Record<number, Set<number>> => {
  const mapa: Record<number, Set<number>> = {};
  (c?.proveedores ?? []).forEach((p) =>
    (p.detalles ?? []).forEach((d) => {
      const id = Number(d.id_inventario);
      (mapa[id] ??= new Set()).add(Number(p.id_proveedor));
    })
  );
  return mapa;
};

/**
 * id_inventario con al menos un proveedor que respondió SIN disponibilidad
 * y que aún no está seleccionado ni autorizado con ningún proveedor.
 */
export const obtenerInventariosSinDisponibilidad = (c?: Cotizacion | null): Set<number> => {
  const sinDisponibilidad = new Set<number>();
  const tomados = new Set<number>();

  (c?.proveedores ?? []).forEach((p) =>
    (p.detalles ?? []).forEach((d) => {
      const id = Number(d.id_inventario);
      const sel = Number(d.seleccionado);
      if (sel === SELECCION.MARCADO || sel === SELECCION.AUTORIZADO) tomados.add(id);
      if (
        Number(d.gestionado) === 1 &&
        Number(d.disponibilidad) === 0 &&
        Number(d.recotizado ?? 0) === 0
      ) {
        sinDisponibilidad.add(id);
      }
    })
  );

  tomados.forEach((id) => sinDisponibilidad.delete(id));
  return sinDisponibilidad;
};

/** Productos que aún no tienen un detalle marcado o autorizado */
export const obtenerProductosPendientes = (
  c: Cotizacion | null | undefined,
  autorizados: Set<number>
) => {
  const marcados = new Set<number>();
  (c?.proveedores ?? []).forEach((p) =>
    p.detalles?.forEach((d) => {
      const sel = Number(d.seleccionado);
      if (sel === SELECCION.MARCADO || sel === SELECCION.AUTORIZADO) {
        marcados.add(Number(d.id_inventario));
      }
    })
  );

  return (c?.productos_requisicion ?? []).filter(
    (p) => !marcados.has(Number(p.id_inventario)) && !autorizados.has(Number(p.id_inventario))
  );
};

/** Deja en cada proveedor solo los detalles que cumplen el filtro y quita los que quedan vacíos */
export const agruparPorProveedor = (
  c: Cotizacion | null | undefined,
  filtro: (d: DetalleCotizacionProveedor) => boolean
): ProveedorCotizacion[] =>
  (c?.proveedores ?? [])
    .map((p) => ({ ...p, detalles: (p.detalles ?? []).filter(filtro) }))
    .filter((p) => p.detalles.length > 0);

export const obtenerIdsMarcados = (c?: Cotizacion | null): number[] =>
  (c?.proveedores ?? []).flatMap((p) =>
    (p.detalles ?? [])
      .filter((d) => Number(d.seleccionado) === SELECCION.MARCADO)
      .map((d) => Number(d.id))
  );

export const esDetalleAutorizadoDisponible = (d: DetalleCotizacionProveedor) =>
  Number(d.seleccionado) === SELECCION.AUTORIZADO && Number(d.disponibilidad) === DISPONIBLE;

export const hayProductosRechazados = (c?: Cotizacion | null) =>
  (c?.productos_requisicion ?? []).some(
    (p) => Number(p.estatus_producto) === ESTATUS_PRODUCTO.RECHAZADO
  );

/** Rechazados que todavía no tienen proveedor elegido para recotizar */
export const obtenerRechazadosSinProveedor = (
  c: Cotizacion | null | undefined,
  recotizacion: Recotizacion
) =>
  (c?.productos_requisicion ?? []).filter(
    (p) =>
      Number(p.estatus_producto) === ESTATUS_PRODUCTO.RECHAZADO &&
      (recotizacion[Number(p.id_inventario)]?.length ?? 0) === 0
  );

export const recotizacionAPayload = (recotizacion: Recotizacion) =>
  Object.entries(recotizacion).map(([id, proveedores]) => ({
    id_inventario: Number(id),
    proveedores,
  }));

export const actualizarRecotizacion = (
  prev: Recotizacion,
  idInventario: number,
  ids: number[]
): Recotizacion => {
  if (ids.length === 0) {
    const { [idInventario]: _, ...resto } = prev;
    return resto;
  }
  return { ...prev, [idInventario]: ids };
};

export const construirDatosBase = (
  c: Cotizacion | null | undefined,
  idUsuario: number,
  descripcion: string
) => ({
  id_usuario: idUsuario,
  id_solicitud: c?.id_requisicion,
  id_cotizacion: c?.id,
  computo: c?.computo ?? "no",
  tipo_computo: c?.computo_tipo ?? "",
  descripcion,
});

export const construirDatosOrden = (
  proveedor: ProveedorCotizacion,
  datosBase: ReturnType<typeof construirDatosBase>
) => ({
  ...proveedor,
  ...datosBase,
  ids_detalle: proveedor.detalles.map((d) => d.id),
  detalles: proveedor.detalles.map((d) => ({
    ...d,
    nombre: d.nombre_producto,
    total: (parseFloat(String(d.cantidad)) * parseFloat(String(d.precio_unitario))).toFixed(2),
  })),
});
